import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AppStateFailureBuilder, AppStateFailureItemWithErrorCode, FailureType } from '@symphony-solo/shared/common-state/src';
import { ApiResponseCode } from '@symphony-solo/shared/http/src';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  addCartItemSuccessAction,
  checkCartItemsAvailabilityAction,
  deleteCartItemSuccessAction,
  getCartAction,
  getCartErrorAction,
  getCartSuccessAction,
  setCartUnAvailabilItemsAction,
} from './cart.actions';
import { CartFacade } from './cart.facade';
import { ChooseLocationFacade } from '@symphony-solo/domain/choose-location/src/lib/+state/choose-location.facade';
import { LocationMenuItemTypes } from '@symphony-solo/domain/choose-location/src/lib/models';

@Injectable()
export class CartEffects {
  constructor(private action$: Actions, private cartFacade: CartFacade, private chooseLocationFacade: ChooseLocationFacade) {}

  protected getCart$ = createEffect(() =>
    this.action$.pipe(
      ofType(getCartAction),
      switchMap(() => {
        return this.cartFacade.cart$.pipe(
          map((cart) => getCartSuccessAction({ cart: cart })),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on getting cart', ApiResponseCode.Server_Error))
              .build();
            return of(getCartErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected addCartItemSuccess$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(addCartItemSuccessAction),
        tap(() => {
          this.cartFacade.setCartStartTime();
          // console.log('Calling addCartItemSuccess cartStartTime');
        })
      ),
    { dispatch: false }
  );

  protected deleteCartItemSuccessAction$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(deleteCartItemSuccessAction),
        withLatestFrom(this.cartFacade.cart$),
        tap(([action, cart]) => {
          const productFound = cart.find((p) => p.productID === action.cart.productID);
          if (!productFound) {
            this.chooseLocationFacade.removeAcceptedAgeRestrictionYear(action.cart.restrictedAge);
          }
        })
      ),
    { dispatch: false }
  );

  protected checkCartItemsAvailability$ = createEffect(() => {
    return this.action$.pipe(
      ofType(checkCartItemsAvailabilityAction),
      withLatestFrom(this.chooseLocationFacade.locationMenus$),
      switchMap(([action, { locationMenus }]) => {
        return this.cartFacade.cart$.pipe(
          map((cartItems) => {
            const cartUnavalibleItems = [];
            for (const cartItem of cartItems) {
              const menu = locationMenus.find((menuItem) => menuItem.id === cartItem.menuID);
              let isCartItemFound = false;
              if (menu) {
                for (const menuItem of menu.items) {
                  if (menuItem.itemType === LocationMenuItemTypes.Course) {
                    const productFound = menuItem.childItems.find((product) => product.productID === cartItem.productID);
                    if (productFound) {
                      isCartItemFound = true;
                      if (productFound.stopped) {
                        cartUnavalibleItems.push(cartItem);
                      }
                    }
                  } else {
                    if (menuItem.productID === cartItem.productID) {
                      isCartItemFound = true;
                      if (menuItem.stopped) {
                        cartUnavalibleItems.push(cartItem);
                      }
                    }
                  }
                }
              }

              if (!isCartItemFound) {
                cartUnavalibleItems.push(cartItem);
              }
            }

            return setCartUnAvailabilItemsAction({ items: cartUnavalibleItems });
          })
        );
      })
    );
  });
}
